import type { ComposeSenderInfo } from './composeTemplate'
/**
 * Signatur beim Schreiben – Standortwahl, Vorauswahl und Darstellung.
 *
 * Bisher lebte die Standort-Logik nur in „Neue Nachricht“; das Antwortfeld
 * holte eine feste Signatur ohne Auswahl. Kundenfeedback 25.10.2026 Teil 3,
 * I: beim Antworten dieselbe Signatur-Auswahl und -Voreinstellung wie bei
 * neuen E-Mails. Beide Wege nutzen jetzt dieses Composable.
 */
import { ref } from 'vue'
import {
  loadUserLocations as fetchUserLocations,
  loadSignatureDefaults,
  renderUserSignature,
  resolveSignatureContext,
  type SignatureDefaults,
  type SignatureLocation,
} from '@/services/emailSignature'
import { useAuthStore } from '@/stores/authStore'

export function useComposeSignature () {
  const userLocations = ref<SignatureLocation[]>([])
  const signatureLocationId = ref<number | null>(null)
  /** Über den Sitzungs-Standort aufgelöster Standort, wenn der Nutzer keinem zugeordnet ist. */
  const fallbackLocation = ref<SignatureLocation | null>(null)
  const orgSignatureTemplate = ref('')
  const orgDefaultSignature = ref('')
  const orgName = ref('')
  const orgHomepage = ref('')
  const companyWebsite = ref('')
  const signatureCompanyName = ref('')
  const signaturePhotoUrl = ref('')
  let loading: Promise<void> | null = null
  /** Organisations-Signatur geladen (auch über loadOrgSignature direkt, „Neue Nachricht“). */
  let loaded = false

  /** Standorte des Nutzers; Vorauswahl: Sitzungs-Standort, sonst der erste. */
  async function loadUserLocations (): Promise<void> {
    const store = useAuthStore()
    userLocations.value = await fetchUserLocations()
    const session = store.idLocation ?? null
    const ids = userLocations.value.map(l => l.id)
    signatureLocationId.value = session !== null && ids.includes(session)
      ? session
      : (ids[0] ?? session)
  }

  function signatureLocation (): SignatureLocation | null {
    return userLocations.value.find(l => l.id === signatureLocationId.value)
      || fallbackLocation.value
      || null
  }

  function signatureDefaults (): SignatureDefaults {
    return {
      signature: orgDefaultSignature.value,
      orgName: orgName.value,
      orgHomepage: orgHomepage.value,
    }
  }

  /**
   * Vorlage für den gewählten Standort: Unternehmens-Signatur vor
   * Organisations-Signatur; der Standort bestimmt das Unternehmen.
   */
  async function resolveSignatureForLocation (): Promise<void> {
    const ctx = await resolveSignatureContext(signatureLocation(), signatureDefaults())
    orgSignatureTemplate.value = ctx.template
    companyWebsite.value = ctx.website
    signatureCompanyName.value = ctx.companyName
    signaturePhotoUrl.value = ctx.photoUrl || ''
    if (!userLocations.value.some(l => l.id === signatureLocationId.value)) {
      fallbackLocation.value = ctx.location
    }
  }

  async function loadOrgSignature (): Promise<void> {
    const defaults = await loadSignatureDefaults(useAuthStore().idOrganization)
    orgDefaultSignature.value = defaults.signature
    orgName.value = defaults.orgName
    orgHomepage.value = defaults.orgHomepage
    await resolveSignatureForLocation()
    loaded = true
  }

  /**
   * Einmal je Composer laden (Antwortfeld: beim ersten Schreiben). Ein schon
   * gewählter Standort bleibt dabei unangetastet.
   */
  function ensureSignatureLoaded (): Promise<void> {
    if (loaded) {
      return Promise.resolve()
    }
    loading ??= (async () => {
      await loadUserLocations()
      await loadOrgSignature()
    })().catch(() => {
      loading = null
    })
    return loading
  }

  /** Standortwechsel: Vorlage neu auflösen (der Aufrufer tauscht die Signatur im Text). */
  async function setSignatureLocation (idLocation: number): Promise<void> {
    signatureLocationId.value = idLocation
    await resolveSignatureForLocation()
  }

  /** Fertige Signatur (HTML) für den gewählten Standort; leer ohne Vorlage. */
  function getRenderedSignature (): string {
    return renderUserSignature(
      {
        template: orgSignatureTemplate.value,
        companyName: signatureCompanyName.value,
        website: companyWebsite.value,
        location: signatureLocation(),
        photoUrl: signaturePhotoUrl.value,
      },
      signatureDefaults(),
    )
  }

  function locationLabel (loc: SignatureLocation | null | undefined): string {
    if (!loc) {
      return ''
    }
    return loc.name || [loc.streetNo, loc.city].filter(Boolean).join(', ') || `Standort ${loc.id}`
  }

  /** Absender-Angaben für Vorlagen-Platzhalter (composeTemplateValues). */
  function senderInfo (): ComposeSenderInfo {
    const store = useAuthStore()
    return {
      user: {
        title: store.title,
        firstName: store.firstName,
        lastName: store.lastName,
        email: store.email,
        mobile: store.signatureMobile,
        landline: store.landline,
        userName: store.userName,
        organizationName: store.organizationName,
      },
      companyName: signatureCompanyName.value,
      orgName: orgName.value,
      website: companyWebsite.value,
      orgHomepage: orgHomepage.value,
      location: signatureLocation(),
    }
  }

  return {
    userLocations,
    signatureLocationId,
    fallbackLocation,
    orgSignatureTemplate,
    orgDefaultSignature,
    orgName,
    orgHomepage,
    companyWebsite,
    signatureCompanyName,
    loadUserLocations,
    loadOrgSignature,
    resolveSignatureForLocation,
    ensureSignatureLoaded,
    setSignatureLocation,
    signatureLocation,
    signatureDefaults,
    getRenderedSignature,
    locationLabel,
    senderInfo,
  }
}

export type ComposeSignature = ReturnType<typeof useComposeSignature>
